import { useMemo, useRef } from 'react';
import { Form as FormType } from '../../types/schema';
import type { tGroupedEquipment } from '../../types';
import { SingleUnit } from './equipmentTypes';
import { formatDateDisplay } from '../../utils/helperFunctions';
import { useFormsContext } from '../../contexts/FormsContext';
import { useUserContext } from '../../contexts/UserContext';
import { useRegionAndLocationContext } from '../../contexts/RegionAndLocationContext';
import { useEquipmentContext } from '../../contexts/EquipmentContext';
import {
    EQUIPMENT_SECTION_CONFIGS,
    ADD_EQUIPMENT_TYPE_OPTION_LABEL,
    ADD_EQUIPMENT_TYPE_OPTION_VALUE,
} from './equipmentConstants';
import { EquipmentFieldConfigs, UnitRecord } from './equipmentTypes';
import {
    formatAcquisitionCost,
    filterEquipmentInspectionForms,
    sortRecordsByName,
    resolveUnitCategoryName,
    resolveUnitCategoryFormId,
    getEffectiveUMCounterReading,
    formatReplacedUMCounterDisplay,
    calculateItdUtilizationMetrics,
    calculateItdUtilizationPacePercent,
    buildCompanyLocationSelectOptions,
    formatCompanyLocationDisplayLabel,
} from './equipmentFieldUtils';
import { displayUnitOfMeasure } from '../../utils/unitOfMeasureUtils';
import { formatStoredYearForDisplay } from '../../utils/dateInputUtils';
import { useCompanyUnitsOfMeasure } from '../../hooks/useCompanyUnitsOfMeasure';
import { useCompanyServiceCenters } from '../../hooks/useCompanyServiceCenters';
import { sortVendorsByName } from '../../utils/companyResourceUtils';
import { getUnitNotes } from '../../utils/unitNotes';

const buildItdUtilizationInput = (unit: SingleUnit) => {
    const record = unit as UnitRecord;
    return {
        putInServiceDate: unit.putInServiceDate,
        aquisitionDate: unit.aquisitionDate,
        inputDate: unit.inputDate,
        lastInspectionDate: unit.lastInspectionDate,
        lastDailyServiceDate: unit.lastDailyServiceDate,
        lastInspectionUMCounter: record.lastInspectionUMCounter as number | null | undefined,
        lastDailyServiceUMCounter: record.lastDailyServiceUMCounter as number | null | undefined,
        beginningUmAtService: unit.machineUMCounterAtAquisition,
        currentUMCounterValue: unit.currentUMCounterValue,
        replacedUmCounterValue: unit.replacedUMCounterValue,
        annualProjectedUsage: unit.annualProjectedUsage,
        adjustmentDays: record.utilizationAdjustmentDays as number | null | undefined,
    };
};

interface EquipmentFieldConfigData {
    formsList: FormType[];
    companyDailyServiceForms: FormType[];
    allCompanyUsers: Array<{ name?: string | null }>;
    companyRegions: Array<{ id: string; name: string }>;
    companyLocations: Array<{
        id: string;
        name: string;
        regionalDivisionId?: string | null;
        districtId?: string | null;
    }>;
    companyDistricts: Array<{ id: string; name: string }>;
    companyGroupedEquipmentData: tGroupedEquipment[];
    inspectionForms: FormType[];
    umCatalog: Array<{ value: string; label: string }>;
    serviceCenters: Array<{ id: string; name?: string | null; archived?: boolean | null }>;
}

export const useEquipmentUnitFieldConfigs = (formsList: FormType[]) => {
    const { companyDailyServiceForms, companyForms } = useFormsContext();
    const { allCompanyUsers, company, companyOwner } = useUserContext();
    const { companyRegions, companyLocations, companyDistricts } = useRegionAndLocationContext();
    const { companyGroupedEquipmentData, companyUnits } = useEquipmentContext();
    const companyId = (company?.id as string) || companyOwner?.companyId || '';
    const umExtraValues = useMemo(
        () =>
            companyUnits
                .map((unit) => unit.unitOfMeasure)
                .filter((value): value is string => Boolean(value?.trim())),
        [companyUnits],
    );
    const { catalogOptions } = useCompanyUnitsOfMeasure(companyId, umExtraValues);
    const { serviceCenters, reloadServiceCenters } = useCompanyServiceCenters(companyId);

    const inspectionForms = useMemo(
        () => sortRecordsByName(filterEquipmentInspectionForms(companyForms)),
        [companyForms],
    );

    const dynamicDataRef = useRef<EquipmentFieldConfigData>({
        formsList,
        companyDailyServiceForms,
        allCompanyUsers,
        companyRegions,
        companyLocations,
        companyDistricts,
        companyGroupedEquipmentData,
        inspectionForms,
        umCatalog: catalogOptions,
        serviceCenters,
    });

    dynamicDataRef.current = {
        formsList,
        companyDailyServiceForms,
        allCompanyUsers,
        companyRegions,
        companyLocations,
        companyDistricts,
        companyGroupedEquipmentData,
        inspectionForms,
        umCatalog: catalogOptions,
        serviceCenters,
    };

    const sectionConfigs = EQUIPMENT_SECTION_CONFIGS;

    const fieldConfigs = useMemo<EquipmentFieldConfigs>(
        () => ({
            'asset-details': [
                {
                    key: 'category',
                    label: 'Asset Category',
                    type: 'custom',
                    editable: false,
                    persisted: false,
                    getValue: (unit) =>
                        resolveUnitCategoryName(unit, dynamicDataRef.current.companyGroupedEquipmentData),
                },
                { key: 'name', label: 'Unit Name / ID *', type: 'input' },
                { key: 'description', label: 'Description', type: 'textarea' },
                {
                    key: 'image',
                    label: 'Photo Thumbnail',
                    type: 'custom',
                    editable: false,
                    persisted: false,
                    getValue: (unit) => (unit.image ? 'Yes' : '-'),
                },
                {
                    key: 'inputDate',
                    label: 'Input Date',
                    type: 'input',
                    editable: false,
                    getValue: (unit) => formatDateDisplay(unit.inputDate),
                },
                {
                    key: 'inputByPerson',
                    label: 'Input By',
                    type: 'input',
                    editable: false,
                    getValue: (unit) => unit.inputByPerson || '-',
                },
                {
                    key: 'enrollment',
                    label: 'Enrollment Eligibility',
                    type: 'custom',
                    editable: false,
                    persisted: false,
                    getValue: (unit) =>
                        [
                            resolveUnitCategoryFormId(unit, dynamicDataRef.current.companyGroupedEquipmentData) &&
                                'Inspections',
                            unit.dailyServiceLogFormId && 'Daily Service',
                            unit.pmModuleEnrollment && 'PM Service',
                            unit.costTrackingModuleEnrollment && 'Cost Tracking',
                            unit.tiresModuleEnrollment && 'Tire Maintenance',
                        ]
                            .filter(Boolean)
                            .join('; ') || '-',
                },
            ],
            manufacturing: [
                {
                    key: 'manufactureDate',
                    label: 'Date Manufactured',
                    type: 'date',
                    dateMode: 'year',
                    getValue: (unit) => formatStoredYearForDisplay(unit.manufactureDate) || '-',
                },
                { key: 'manufacturerName', label: 'Manufacturer', type: 'search-suggest' },
                { key: 'modelNumber', label: 'Model #', type: 'search-suggest' },
                {
                    key: 'serialNumber',
                    label: 'Unit Serial #',
                    type: 'input',
                    hint: 'Manufacturer serial / VIN. Cannot match another unit or engine serial.',
                },
                {
                    key: 'licenseNumber',
                    label: 'License #',
                    type: 'input',
                    hint: 'Cannot match another unit in your company.',
                },
                { key: 'engineManufacturer', label: 'Engine Manufacturer', type: 'search-suggest' },
                { key: 'engineModelNumber', label: 'Engine Model #', type: 'search-suggest' },
                {
                    key: 'engineSerialNumber',
                    label: 'Engine Serial #',
                    type: 'input',
                    hint: 'Cannot match another unit or engine serial.',
                },
                {
                    key: 'aquisitionDate',
                    label: 'Date Acquired',
                    type: 'date',
                    getValue: (unit) => formatDateDisplay(unit.aquisitionDate),
                },
                {
                    key: 'putInServiceDate',
                    label: 'Date Placed in Service',
                    type: 'date',
                    getValue: (unit) => formatDateDisplay(unit.putInServiceDate),
                },
                {
                    key: 'aquisitionCost',
                    label: 'Acquisition Cost',
                    type: 'number',
                    currency: true,
                    getValue: (unit) => {
                        const formatted = formatAcquisitionCost(unit.aquisitionCost);
                        return formatted === '-' ? '-' : `$${formatted}`;
                    },
                },
            ],
            utilization: [
                {
                    key: 'unitOfMeasure',
                    label: 'UM Description',
                    type: 'search-suggest',
                    getValue: (unit) =>
                        displayUnitOfMeasure(unit.unitOfMeasure, dynamicDataRef.current.umCatalog) || '-',
                },
                {
                    key: 'machineUMCounterAtAquisition',
                    label: 'Beg. UM Value',
                    type: 'number',
                    min: 0,
                    getLabel: (unit) =>
                        `Beg. ${displayUnitOfMeasure(unit.unitOfMeasure, dynamicDataRef.current.umCatalog) || 'Hours'}`,
                    getValue: (unit) =>
                        unit.machineUMCounterAtAquisition != null
                            ? unit.machineUMCounterAtAquisition.toLocaleString()
                            : '-',
                },
                { key: 'annualProjectedUsage', label: 'Annual Projected Utilization', type: 'number', min: 0 },
                {
                    key: 'utilizationAdjustmentDays',
                    label: 'Adjustment Days',
                    type: 'number',
                    min: 0,
                    hint: 'Days unavailable for service (e.g. body shop, major repair). Subtracted from gross days in service for ITD Utilization %.',
                },
                {
                    key: 'itdGrossDays',
                    label: 'Gross Days in Service',
                    type: 'custom',
                    editable: false,
                    persisted: false,
                    getValue: (unit) => {
                        const metrics = calculateItdUtilizationMetrics(buildItdUtilizationInput(unit));
                        return metrics ? metrics.grossDays.toLocaleString() : '-';
                    },
                },
                {
                    key: 'itdNetDays',
                    label: 'Net Days Available',
                    type: 'custom',
                    editable: false,
                    persisted: false,
                    getValue: (unit) => {
                        const metrics = calculateItdUtilizationMetrics(buildItdUtilizationInput(unit));
                        return metrics ? metrics.netDays.toLocaleString() : '-';
                    },
                },
                {
                    key: 'replacedUMCounterValue',
                    label: 'Replaced Counter',
                    type: 'custom',
                    editable: false,
                    persisted: true,
                    getValue: (unit) => formatReplacedUMCounterDisplay(unit as UnitRecord),
                    getLabel: (unit) =>
                        `Replaced ${displayUnitOfMeasure(unit.unitOfMeasure, dynamicDataRef.current.umCatalog) || 'Hours'} Counter`,
                },
                {
                    key: 'currentUMCounterValue',
                    label: 'Current UM Counter',
                    type: 'custom',
                    editable: false,
                    getValue: (unit) =>
                        unit.currentUMCounterValue != null
                            ? unit.currentUMCounterValue.toLocaleString()
                            : 'Updated at inspection/service',
                    getLabel: (unit) =>
                        `Current ${displayUnitOfMeasure(unit.unitOfMeasure, dynamicDataRef.current.umCatalog) || 'Hours'} Counter`,
                },
                {
                    key: 'effectiveUMCounter',
                    label: 'Effective Counter',
                    type: 'custom',
                    editable: false,
                    persisted: false,
                    getValue: (unit) => {
                        const effective = getEffectiveUMCounterReading(unit);
                        return effective != null ? effective.toLocaleString() : '-';
                    },
                    getLabel: (unit) =>
                        `Effective ${displayUnitOfMeasure(unit.unitOfMeasure, dynamicDataRef.current.umCatalog) || 'Hours'} Counter (current + replaced)`,
                },
                {
                    key: 'totalUM',
                    label: 'Total UM',
                    type: 'custom',
                    editable: false,
                    persisted: false,
                    getValue: (unit) => {
                        const effective = getEffectiveUMCounterReading(unit);
                        if (effective == null) return '-';
                        const beg = unit.machineUMCounterAtAquisition || 0;
                        const usage = effective - beg;
                        return Number.isFinite(usage) ? usage.toLocaleString() : '-';
                    },
                    getLabel: (unit) =>
                        `Total ${displayUnitOfMeasure(unit.unitOfMeasure, dynamicDataRef.current.umCatalog) || 'Hours'} (since beginning value)`,
                },
                {
                    key: 'itdUtilization',
                    label: 'ITD Utilization %',
                    type: 'custom',
                    editable: false,
                    persisted: false,
                    getValue: (unit) => {
                        const pacePercent = calculateItdUtilizationPacePercent(buildItdUtilizationInput(unit));
                        return pacePercent != null ? `${pacePercent.toFixed(1)}%` : '-';
                    },
                    getLabel: (_unit) => `ITD Utilization %`,
                },
            ],
            'inspection-summary': [
                {
                    key: 'lastInspectionDate',
                    label: 'Last Inspection Date',
                    type: 'input',
                    editable: false,
                    getValue: (unit) => formatDateDisplay(unit.lastInspectionDate) || '-',
                },
                {
                    key: 'lastInspectionUMCounter',
                    label: 'Last Inspection UM',
                    type: 'custom',
                    editable: false,
                    persisted: false,
                    getValue: (unit) => {
                        const reading = (unit as UnitRecord).lastInspectionUMCounter;
                        return reading != null ? reading.toLocaleString() : '-';
                    },
                    getLabel: (unit) =>
                        `Last Inspection ${displayUnitOfMeasure(unit.unitOfMeasure, dynamicDataRef.current.umCatalog) || 'UM'}`,
                },
                {
                    key: 'inspectionFormName',
                    label: 'Inspection Form',
                    type: 'custom',
                    editable: false,
                    persisted: false,
                    getValue: (unit) =>
                        dynamicDataRef.current.formsList.find(
                            (form) =>
                                form.id ===
                                resolveUnitCategoryFormId(
                                    unit,
                                    dynamicDataRef.current.companyGroupedEquipmentData,
                                ),
                        )?.name || '-',
                },
            ],
            'daily-service-summary': [
                {
                    key: 'lastDailyServiceDate',
                    label: 'Last Daily Service Date',
                    type: 'input',
                    editable: false,
                    getValue: (unit) => formatDateDisplay(unit.lastDailyServiceDate),
                },
                {
                    key: 'lastDailyServiceUMCounter',
                    label: 'Last Daily Service UM',
                    type: 'custom',
                    editable: false,
                    persisted: false,
                    getValue: (unit) =>
                        unit.lastDailyServiceUMCounter != null ? unit.lastDailyServiceUMCounter.toLocaleString() : '-',
                    getLabel: (unit) =>
                        `Last Daily Service ${displayUnitOfMeasure(unit.unitOfMeasure, dynamicDataRef.current.umCatalog) || 'UM'}`,
                },
                {
                    key: 'dailyServiceLogFormId',
                    label: 'Daily Service Form',
                    type: 'select',
                    editable: true,
                    getValue: (unit) =>
                        dynamicDataRef.current.companyDailyServiceForms.find(
                            (form) => form.id === unit.dailyServiceLogFormId,
                        )?.name || '-',
                    getOptions: () => [
                        { label: '— None —', value: '' },
                        ...sortRecordsByName(dynamicDataRef.current.companyDailyServiceForms).map((form) => ({
                            label: form.name,
                            value: form.id,
                        })),
                    ],
                },
            ],
            'assignment-organization': [
                {
                    key: 'assignedTo',
                    label: 'Assigned To',
                    type: 'select',
                    editable: true,
                    getOptions: () => [
                        { label: '— None —', value: '' },
                        ...dynamicDataRef.current.allCompanyUsers
                            .filter((u) => u.name)
                            .sort((a, b) => a.name!.localeCompare(b.name!))
                            .map((u) => ({ label: u.name!, value: u.name! })),
                    ],
                },
                {
                    key: 'locationId',
                    label: 'Location',
                    type: 'select',
                    editable: true,
                    getValue: (unit) =>
                        formatCompanyLocationDisplayLabel(
                            unit.locationId,
                            dynamicDataRef.current.companyRegions,
                            dynamicDataRef.current.companyLocations,
                            dynamicDataRef.current.companyDistricts,
                        ),
                    getOptions: () =>
                        buildCompanyLocationSelectOptions(
                            dynamicDataRef.current.companyRegions,
                            dynamicDataRef.current.companyLocations,
                            dynamicDataRef.current.companyDistricts,
                        ),
                },
                {
                    key: 'serviceCenterId',
                    label: 'Service Center',
                    type: 'select',
                    editable: true,
                    getValue: (unit) => {
                        const serviceCenterId = (unit as UnitRecord).serviceCenterId as string | null | undefined;
                        if (!serviceCenterId) return '-';
                        return (
                            dynamicDataRef.current.serviceCenters.find((center) => center.id === serviceCenterId)?.name ||
                            '-'
                        );
                    },
                    getOptions: () => [
                        { label: '— None —', value: '' },
                        ...sortVendorsByName(
                            dynamicDataRef.current.serviceCenters.filter((center) => !center.archived),
                        ).map((center) => ({
                            label: center.name || 'Service center',
                            value: center.id,
                        })),
                    ],
                },
            ],
            'lifecycle-status': [
                {
                    key: 'removedFromServiceDate',
                    label: 'Removed from Service Date',
                    type: 'date',
                    getValue: (unit) => formatDateDisplay(unit.removedFromServiceDate),
                },
                { key: 'removedFromServiceReason', label: 'Removed from Service Reason', type: 'input' },
                {
                    key: 'dispositionDate',
                    label: 'Disposition Date',
                    type: 'date',
                    getValue: (unit) => formatDateDisplay(unit.dispositionDate),
                },
                { key: 'dispositionReason', label: 'Disposition Reason', type: 'input' },
                {
                    key: 'notes',
                    label: 'Notes',
                    type: 'textarea',
                    persisted: false,
                    editable: true,
                    getValue: (unit) => getUnitNotes(unit.extraUnitInfo) || '-',
                },
                {
                    key: 'equipmentStatus',
                    label: 'Archived',
                    type: 'toggle',
                    editable: true,
                    getValue: (unit) => (unit.equipmentStatus ? 'No' : 'Yes'),
                },
                {
                    key: 'archivedDate',
                    label: 'Archived Date',
                    type: 'custom',
                    editable: false,
                    persisted: false,
                    getValue: (unit) =>
                        (unit as UnitRecord).archivedDate ? formatDateDisplay((unit as UnitRecord).archivedDate) : '-',
                },
            ],
        }),
        [],
    );

    const newUnitFieldConfigs = useMemo(() => {
        const assetDetails = fieldConfigs['asset-details'].map((field) => {
            if (field.key === 'category') {
                return {
                    key: 'categoryId',
                    label: 'Asset Category *',
                    type: 'select' as const,
                    editable: true,
                    getOptions: () => [
                        ...dynamicDataRef.current.companyGroupedEquipmentData.map((category) => ({
                            label: category.name,
                            value: category.id,
                        })),
                        {
                            label: ADD_EQUIPMENT_TYPE_OPTION_LABEL,
                            value: ADD_EQUIPMENT_TYPE_OPTION_VALUE,
                        },
                    ],
                };
            }

            if (field.key === 'inputDate') {
                return {
                    ...field,
                    type: 'custom' as const,
                    editable: false,
                    persisted: false,
                    getValue: (unit: SingleUnit) => formatDateDisplay(unit.inputDate),
                };
            }

            if (field.key === 'inputByPerson') {
                return {
                    ...field,
                    type: 'custom' as const,
                    editable: false,
                    persisted: false,
                    getValue: (unit: SingleUnit) => unit.inputByPerson || '-',
                };
            }

            return field;
        });

        const inspectionSummary = fieldConfigs['inspection-summary'].map((field) =>
            field.key === 'inspectionFormName'
                ? {
                      key: 'inspectionFormId',
                      label: 'Inspection Form',
                      type: 'select' as const,
                      editable: true,
                      persisted: false,
                      getOptions: () => [
                          { label: '— None —', value: '' },
                          ...dynamicDataRef.current.inspectionForms.map((form) => ({
                              label: form.name,
                              value: form.id,
                          })),
                      ],
                  }
                : field,
        );

        return {
            ...fieldConfigs,
            'asset-details': assetDetails,
            'inspection-summary': inspectionSummary,
        };
    }, [fieldConfigs]);

    const editUnitFieldConfigs = useMemo(() => {
        const inspectionSummary = fieldConfigs['inspection-summary'].map((field) =>
            field.key === 'inspectionFormName'
                ? {
                      key: 'inspectionFormId',
                      label: 'Inspection Form',
                      type: 'select' as const,
                      editable: true,
                      persisted: false,
                      getOptions: () => [
                          { label: '— None —', value: '' },
                          ...dynamicDataRef.current.inspectionForms.map((form) => ({
                              label: form.name,
                              value: form.id,
                          })),
                      ],
                  }
                : field,
        );

        return {
            ...fieldConfigs,
            'inspection-summary': inspectionSummary,
        };
    }, [fieldConfigs]);

    return {
        sectionConfigs,
        fieldConfigs,
        inspectionForms,
        newUnitFieldConfigs,
        editUnitFieldConfigs,
        companyId,
        reloadServiceCenters,
    };
};
